/**
 * Client HTTP unique. Toutes les requetes portent `credentials: 'include'`
 * pour que le cookie de session voyage, et l'erreur renvoyee par l'API est
 * remontee telle quelle : ses messages sont rediges pour etre lus par
 * l'utilisateur, pas pour etre remplaces par un « une erreur est survenue »
 * generique.
 */

export class ErreurApi extends Error {
  readonly statut: number;
  readonly code: string;
  readonly details?: { champ: string; message: string }[];

  constructor(
    statut: number,
    code: string,
    message: string,
    details?: { champ: string; message: string }[],
  ) {
    super(message);
    this.name = 'ErreurApi';
    this.statut = statut;
    this.code = code;
    this.details = details;
  }

  get estNonAuthentifie(): boolean {
    return this.statut === 401;
  }
}

/**
 * LA SESSION PERDUE EN COURS DE ROUTE.
 *
 * `FournisseurAuth` ne lit la session qu'au demarrage. Expiree ensuite —
 * delai d'inactivite, revocation depuis l'ecran Utilisateurs —, elle
 * restait en memoire : le routage laissait passer, chaque appel echouait
 * en 401, et l'ecran affichait un encart d'erreur au lieu de renvoyer a
 * la connexion. Vecu deux fois pendant la mise au point.
 *
 * Le client signale donc tout 401 a qui veut l'entendre, et l'auth vide
 * sa session — le routage fait le reste.
 */
const abonnes = new Set<() => void>();

/** S'abonner a la perte de session. Rend la fonction de desabonnement. */
export function surSessionPerdue(rappel: () => void): () => void {
  abonnes.add(rappel);
  return () => abonnes.delete(rappel);
}

/**
 * Les routes d'AUTHENTIFICATION sont exclues, et ce n'est pas un detail :
 * un mot de passe errone renvoie lui aussi 401, avec le meme code
 * `non_authentifie`. Le chemin est donc le seul discriminant fiable.
 *
 * `/api/auth/session` l'est aussi : au premier chargement sans cookie
 * elle repond 401 legitimement, et `rafraichir` le traite deja — le
 * signaler en plus annoncerait une expiration a qui n'a jamais ouvert de
 * session.
 */
const HORS_SIGNAL = ['/api/auth/session', '/api/auth/connexion', '/api/auth/google'];

function signalerSiSessionPerdue(statut: number, chemin: string): void {
  if (statut !== 401) return;
  if (HORS_SIGNAL.some((prefixe) => chemin.startsWith(prefixe))) return;
  for (const rappel of abonnes) rappel();
}

async function requete<T>(
  methode: string,
  chemin: string,
  corps?: unknown,
): Promise<T> {
  const reponse = await fetch(chemin, {
    method: methode,
    credentials: 'include',
    headers: corps === undefined ? {} : { 'Content-Type': 'application/json' },
    body: corps === undefined ? undefined : JSON.stringify(corps),
  });

  if (reponse.status === 204) {
    return undefined as T;
  }

  const typeContenu = reponse.headers.get('content-type') ?? '';
  const charge = typeContenu.includes('application/json') ? await reponse.json() : null;

  if (!reponse.ok) {
    signalerSiSessionPerdue(reponse.status, chemin);
    const erreur = (charge as { erreur?: { code: string; message: string; details?: never } })
      ?.erreur;
    throw new ErreurApi(
      reponse.status,
      erreur?.code ?? 'erreur_inconnue',
      erreur?.message ?? `La requete a echoue (${reponse.status}).`,
      erreur?.details,
    );
  }

  return charge as T;
}

function avecParametres(chemin: string, parametres?: Record<string, unknown>): string {
  if (!parametres) return chemin;
  const requeteUrl = new URLSearchParams();
  for (const [cle, valeur] of Object.entries(parametres)) {
    if (valeur !== undefined && valeur !== null && valeur !== '') {
      requeteUrl.set(cle, String(valeur));
    }
  }
  const chaine = requeteUrl.toString();
  return chaine ? `${chemin}?${chaine}` : chemin;
}

/**
 * Envoi d'un fichier en corps brut, avec son type MIME reel.
 *
 * Pas de multipart : un seul fichier, aucune autre donnee, et le serveur
 * verifie de toute facon le format par les octets d'en-tete plutot que
 * par ce que le navigateur declare.
 */
async function televerser<T>(chemin: string, fichier: File): Promise<T> {
  const reponse = await fetch(chemin, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': fichier.type || 'application/octet-stream' },
    body: fichier,
  });

  const typeContenu = reponse.headers.get('content-type') ?? '';
  const charge = typeContenu.includes('application/json') ? await reponse.json() : null;

  if (!reponse.ok) {
    signalerSiSessionPerdue(reponse.status, chemin);
    const erreur = (charge as { erreur?: { code: string; message: string } })?.erreur;
    // 413 est renvoye par la couche HTTP avant d'atteindre nos gestionnaires :
    // sans ce cas, l'utilisateur recevrait un message vide.
    const message =
      erreur?.message ??
      (reponse.status === 413
        ? 'Fichier trop volumineux.'
        : `L’envoi a echoue (${reponse.status}).`);
    throw new ErreurApi(reponse.status, erreur?.code ?? 'erreur_envoi', message);
  }

  return charge as T;
}

/**
 * Reception d'un FICHIER produit par le serveur (ADR-0014) : le corps est
 * un blob, le nom vient de `Content-Disposition`. Une erreur arrive en
 * JSON comme ailleurs, et se remonte de la meme facon.
 */
async function telecharger(chemin: string): Promise<{ contenu: Blob; nomFichier: string | null }> {
  const reponse = await fetch(chemin, { credentials: 'include' });
  if (!reponse.ok) {
    signalerSiSessionPerdue(reponse.status, chemin);
    const typeContenu = reponse.headers.get('content-type') ?? '';
    const charge = typeContenu.includes('application/json') ? await reponse.json() : null;
    const erreur = (charge as { erreur?: { code: string; message: string } })?.erreur;
    throw new ErreurApi(
      reponse.status,
      erreur?.code ?? 'erreur_inconnue',
      erreur?.message ?? `Le téléchargement a échoué (${reponse.status}).`,
    );
  }
  const disposition = reponse.headers.get('content-disposition') ?? '';
  const nomFichier = /filename="([^"]+)"/.exec(disposition)?.[1] ?? null;
  return { contenu: await reponse.blob(), nomFichier };
}

export const api = {
  get: <T>(chemin: string, parametres?: Record<string, unknown>) =>
    requete<T>('GET', avecParametres(chemin, parametres)),
  post: <T>(chemin: string, corps?: unknown) => requete<T>('POST', chemin, corps ?? {}),
  patch: <T>(chemin: string, corps: unknown) => requete<T>('PATCH', chemin, corps),
  // PUT : remplacement en bloc, la ou PATCH modifie au champ pres. Le
  // calendrier d'une annee s'enregistre ainsi, grille entiere.
  put: <T>(chemin: string, corps: unknown) => requete<T>('PUT', chemin, corps),
  delete: <T>(chemin: string) => requete<T>('DELETE', chemin),
  televerser,
  telecharger,
};
